import { ArrowLeftOutlined, EditFilled } from "@ant-design/icons";
import { Button, message, Space, Spin, Table, Tag, Tooltip } from "antd";
import _ from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiRequest } from "../../api";
import apiPaths from "../../api/paths";
import { useHasEditPermission } from "../../common/hooks";
import VehicleModal from "./components/VehicleModal";

function Vehicle() {
  const location = useLocation();
  const { id } = location.state;
  const navigate = useNavigate();
  const [editingKey, setEditingKey] = useState(null);
  const [vehicle, setVehicle] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [stations, setStations] = useState([]);
  const [statusHistory, setStatusHistory] = useState([]);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const allowEdit = useHasEditPermission();

  useEffect(() => {
    if (id) {
      async function fetchData() {
        setIsLoading(true);
        const res = await apiRequest({
          path: `/vehicles/${id}`,
        });
        if (res.error) {
          message.error(res.error_description);
          return;
        } else {
          setStatusHistory(res.status_history);
          delete res.status_history;
          setVehicle(res);
        }
        setIsLoading(false);
      }
      fetchData();
    }
  }, [id]);

  useEffect(() => {
    fetchStations();
  }, []);

  const fetchStations = async () => {
    const res = await apiRequest({
      path: apiPaths.stations,
    });
    if (!res.error) {
      setStations(res.results);
    }
  };

  const handleVehicleLock = async (status) => {
    const res = await apiRequest({
      path: apiPaths.vehicles + `/${vehicle.vehicle_number}/lock`,
      method: "POST",
      data: {
        lock: status,
      },
    });
    if (!res.error) {
      let updatedVehicle = { ...vehicle, lock_status: status };
      setVehicle(updatedVehicle);
      return;
    }
    message.error(res.error_description);
  };

  const handleSave = async (row) => {
    let key = editingKey;
    const res = await apiRequest({
      path: apiPaths.vehicles + `/${key}`,
      method: "PUT",
      data: row,
    });
    if (res.error) {
      message.error(res.error_description);
      return;
    }
    setVehicle({ ...vehicle, ...res });
    setEditingKey(null);
    setShowVehicleModal(false);
  };

  return (
    <div style={{ height: "80vh" }}>
      {isLoading ? (
        <Spin />
      ) : (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginRight: 10,
            }}
          >
            <Space>
              <Button
                type="ghost"
                icon={<ArrowLeftOutlined />}
                onClick={() => {
                  navigate(-1);
                }}
              />
              <div style={{ fontSize: 28 }}>{vehicle.vehicle_number}</div>
            </Space>
            {allowEdit ? (
              <Space>
                <Tooltip title="Edit Vehicle">
                  <Button
                    type="primary"
                    icon={<EditFilled />}
                    onClick={() => {
                      setEditingKey(id);
                      setShowVehicleModal(true);
                    }}
                  />
                </Tooltip>
              </Space>
            ) : null}
          </div>
          <div style={{ padding: 15 }}>
            <Space style={{ flexWrap: "wrap", marginBottom: 25 }}>
              <Space>
                <div style={{ color: "#a7a7a7" }}>IMEI Number:</div>
                <div>{vehicle.imei_number}</div>
              </Space>
              <Space>
                <div style={{ color: "#a7a7a7" }}>Type:</div>
                <div>{vehicle.type}</div>
              </Space>
              <Space>
                <div style={{ color: "#a7a7a7" }}>Price / Min:</div>
                <div>{vehicle.price_per_min}</div>
              </Space>
              {vehicle.location ? (
                <Space>
                  <div style={{ color: "#a7a7a7" }}>Current Location:</div>
                  <div>{vehicle.location?.coordinates.join(", ")}</div>
                </Space>
              ) : null}
              <Space>
                <div style={{ color: "#a7a7a7" }}>MFG Date:</div>
                <div>
                  {vehicle.mfg_date
                    ? moment(vehicle.mfg_date).format("DD-MM-YYYY HH:mm")
                    : "Not Given"}
                </div>
              </Space>
              <Space>
                <div style={{ color: "#a7a7a7" }}>Dock Station:</div>
                <div>
                  <div>{vehicle.dock_station?.name || "NA"}</div>
                </div>
              </Space>
              <Space>
                <div style={{ color: "#a7a7a7" }}>Loack Status:</div>
                <Tag color={vehicle.lock_status ? "red" : "green"}>
                  {vehicle.lock_status ? "Locked" : "Un Locked"}
                </Tag>
                {allowEdit ? (
                  <div style={{ marginTop: 5 }}>
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => {
                        handleVehicleLock(!vehicle.lock_status);
                      }}
                    >
                      {`${vehicle.lock_status ? "Un Lock" : "Lock"} It`}
                    </Button>
                  </div>
                ) : null}
              </Space>
              <Space>
                <div style={{ color: "#a7a7a7" }}>Created At:</div>
                <div>
                  <div>{vehicle.created_by?.fullname}</div>
                  <div style={{ color: "#a7a7a7", fontSize: 11 }}>
                    {moment(vehicle.created_at).fromNow()}
                  </div>
                </div>
              </Space>
              <Space>
                <div style={{ color: "#a7a7a7" }}>Last Update:</div>
                <div>
                  <div>
                    {vehicle.updated_by
                      ? vehicle.updated_by?.fullname
                      : vehicle.created_by?.fullname}
                  </div>
                  <div style={{ color: "#a7a7a7", fontSize: 11 }}>
                    {moment(vehicle.updated_at).fromNow()}
                  </div>
                </div>
              </Space>
            </Space>
            <div>Status History</div>
            <Table
              dataSource={statusHistory}
              columns={[
                {
                  title: "Status",
                  dataIndex: "status",
                  width: "150px",
                },
                {
                  title: "Remark",
                  dataIndex: "remark",
                },
                {
                  title: "Created By",
                  dataIndex: "created_by",
                  render: (value, record) => {
                    return (
                      record.created_by?.fullname || record.created_by?.username
                    );
                  },
                },
                {
                  title: "Created At",
                  dataIndex: "created_at",
                  render: (value) => moment(value).format("DD-MM-YYYY HH:mm"),
                },
              ]}
            />
          </div>
          {showVehicleModal ? (
            <VehicleModal
              open={showVehicleModal}
              onSave={handleSave}
              onCancel={() => {
                setShowVehicleModal(false);
              }}
              data={vehicle}
              isUpdate={editingKey}
              stations={stations}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

export default Vehicle;
